import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertCircle, ChevronDown, Star, Loader2, Pause, Play, Repeat, SkipBack, SkipForward, X } from 'lucide-react';
import { RECITERS, byName, clipLabel, photoOf, reciterById, type Reciter } from '@/data/reciters';
import { useSettings } from '@/lib/store';
import { chapter } from '@/features/quran/api';
import { Chip, NumberField, Range, RepeatStepper, Row, Section, Segmented, Sheet, Stepper, Switch, haptic } from '@/components/ui';
import { useCanHear } from './access';
import { isReciting, usePlayer } from './audioEngine';

const mmss = (ms: number) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

export function ReciterAvatar({ r, size = 40 }: { r: Reciter; size?: number }) {
  const [broken, setBroken] = useState(false);
  const showPhotos = useSettings((s) => s.settings.showReciterPhotos);
  const photo = photoOf(r);
  if (photo && showPhotos && !broken) {
    return <img src={photo.src} alt="" width={size} height={size} loading="lazy" decoding="async" onError={() => setBroken(true)}
      className="shrink-0 rounded-full object-cover object-top ring-1 ring-hairline" style={{ width: size, height: size }} />;
  }
  const initials = r.name.replace(/^(Hajjah|Sheikh|Shaykh)\s+/i, '').split(/\s+/).slice(0, 2).map((p) => p[0]).join('');
  const hue = [...r.id].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <span className="grid shrink-0 place-items-center rounded-full font-bold text-white" aria-hidden
      style={{ width: size, height: size, fontSize: size * 0.36, background: `linear-gradient(135deg, hsl(${hue} 45% 42%), hsl(${(hue + 40) % 360} 50% 28%))` }}>
      {initials}
    </span>
  );
}

export function MiniPlayer() {
  const p = usePlayer();
  useSettings((s) => s.settings.repeat); // re-render when recite mode changes
  const reciting = isReciting(p.range);
  if (p.status === 'idle' || !p.surah) return null;
  const r = reciterById(p.reciterId);
  const busy = p.status === 'loading';
  const playing = p.status === 'playing' || p.status === 'gap';
  return (
    <>
      <div className="glass fixed inset-x-2 z-40 flex items-center gap-3 rounded-[22px] py-2 pl-2 pr-2 sm:left-1/2 sm:right-auto sm:w-[560px] sm:-translate-x-1/2"
        style={{ bottom: 'calc(var(--tabbar-h) + var(--safe-bottom) + 14px)' }}>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => p.setSheet(true)} aria-label="Open player">
          <ReciterAvatar r={r} size={40} />
          <span className="min-w-0">
            <span className="block truncate text-[15px] font-semibold">{p.track?.clip ? clipLabel(p.track.clip, (s) => chapter(s).n) : p.track?.juz ? `Juz ${p.track.juz}` : chapter(p.surah).n}{p.current.verseKey ? ` · ${p.current.verseKey}` : ''}</span>
            <span className="block truncate text-[13px] text-muted">
              {p.status === 'gap' ? 'Your turn — recite, or tap ▶︎ to continue' : p.status === 'error' ? p.error : r.name}
            </span>
          </span>
        </button>
        <button type="button" aria-label={p.status === 'gap' ? 'Continue' : playing ? 'Pause' : 'Play'} className="press grid h-11 w-11 place-items-center rounded-full bg-accent text-accent-ink"
          onClick={() => { haptic(); if (p.status === 'gap') p.skipGap(); else p.toggle(); }}>
          {busy ? <Loader2 className="animate-spin" size={20} /> : p.status === 'gap' ? <SkipForward size={20} /> : playing ? <Pause size={20} /> : <Play size={20} />}
        </button>
        <button type="button" aria-label="Stop" className="press grid h-9 w-9 place-items-center rounded-full text-muted" onClick={() => p.stop()}><X size={18} /></button>
      </div>
      <NowPlayingSheet />
    </>
  );
}

function NowPlayingSheet() {
  const p = usePlayer();
  const nav = useNavigate();
  const repeat = useSettings((s) => s.settings.repeat);
  const updateDeep = useSettings((s) => s.updateDeep);
  const [pickReciter, setPickReciter] = useState(false);
  if (!p.surah) return null;
  const ch = chapter(p.surah);
  const r = reciterById(p.reciterId);
  const playing = p.status === 'playing' || p.status === 'gap';
  const canSegment = p.track?.granularity !== 'none';
  const reciting = isReciting(p.range);
  const range = p.range ?? { from: 1, to: ch.v };

  const applyRange = (from: number, to: number) => {
    const f = Math.max(1, Math.min(ch.v, from));
    const t = Math.max(f, Math.min(ch.v, to));
    p.setRange({ from: f, to: t });
  };

  return (
    <Sheet open={p.sheetOpen} onClose={() => p.setSheet(false)} title={<span className="flex items-center gap-2"><ChevronDown size={18} /> Now playing</span>}>
      <div className="flex flex-col items-center gap-2 px-5 pb-4 pt-2 text-center">
        <ReciterAvatar r={r} size={84} />
        <div className="mt-2 text-[22px] font-bold">{p.track?.clip ? clipLabel(p.track.clip, (s) => chapter(s).n) : p.track?.juz ? `Juz ${p.track.juz}` : <>{ch.n} <span className="font-arabic font-normal text-gold">{ch.ar}</span></>}</div>
        <button type="button" className="press text-[15px] font-semibold text-accent" onClick={() => setPickReciter(true)}>{r.name} · change</button>
        {p.status === 'gap' && <div className="mt-1 rounded-full bg-gold/15 px-3 py-1 text-[14px] font-semibold text-gold">Your turn — recite the āyah</div>}
        {reciting && (repeat.rangeRepeat !== 1 || repeat.ayahRepeat !== 1) && (
          <div className="mt-1 text-[13px] font-semibold tabular-nums text-muted">
            {repeat.rangeRepeat !== 1 && <>{p.range ? `Āyāt ${range.from}–${range.to}` : 'Surah'} · pass {p.rangePass + 1}{repeat.rangeRepeat ? ` of ${repeat.rangeRepeat}` : ' · ∞'}</>}
            {repeat.rangeRepeat !== 1 && repeat.ayahRepeat !== 1 && '  ·  '}
            {repeat.ayahRepeat !== 1 && <>āyah {p.ayahPass + 1}{repeat.ayahRepeat ? `/${repeat.ayahRepeat}` : ' · ∞'}</>}
          </div>
        )}
        {p.error && <div className="mt-1 flex items-center gap-2 text-[14px] text-danger"><AlertCircle size={16} /> {p.error}</div>}
        {!canSegment && p.track && (
          <p className="mt-1 text-[13px] text-muted">Āyah timings for this recording haven't been generated yet, so it plays straight through. Highlighting and repeat need timings.</p>
        )}
      </div>

      <div className="px-6">
        <input type="range" aria-label="Seek" className="range" min={0} max={Math.max(1, p.durationMs)} value={p.positionMs}
          style={{ ['--pct' as string]: `${(p.positionMs / Math.max(1, p.durationMs)) * 100}%` }} onChange={(e) => p.seekMs(Number(e.target.value))} />
        <div className="flex justify-between text-[12px] tabular-nums text-muted"><span>{mmss(p.positionMs)}</span><span>{mmss(p.durationMs)}</span></div>
      </div>

      <div className="flex items-center justify-center gap-6 py-4">
        <button type="button" aria-label="Previous āyah" className="press grid h-12 w-12 place-items-center rounded-full bg-fill" onClick={() => p.prevAyah()} disabled={!canSegment}><SkipBack size={22} /></button>
        <button type="button" aria-label={playing ? 'Pause' : 'Play'} className="press grid h-16 w-16 place-items-center rounded-full bg-accent text-accent-ink" onClick={() => p.toggle()}>
          {p.status === 'loading' ? <Loader2 size={28} className="animate-spin" /> : playing ? <Pause size={28} /> : <Play size={28} />}
        </button>
        <button type="button" aria-label="Next āyah" className="press grid h-12 w-12 place-items-center rounded-full bg-fill" onClick={() => p.nextAyah()} disabled={!canSegment}><SkipForward size={22} /></button>
      </div>

      <Section title="Recite along" footer={reciting
        ? 'Pauses after each āyah so you can recite it back, with optional repeats — ideal for ḥifẓ. Logged as recitation time.'
        : 'Off — the surah plays straight through. Switch on to pause after each āyah and repeat.'}>
        <Row icon={<Repeat size={18} />} label="Recite mode"><Switch label="Recite mode" checked={reciting} onChange={(v) => p.setReciteMode(v)} /></Row>
        {p.status === 'gap' && <Row label="Skip this pause" onClick={() => p.skipGap()} />}
        {reciting && (
          <>
            <div className="row flex-col !items-stretch gap-2">
              <span className="text-[15px]">Pause after each āyah</span>
              <Segmented label="Gap" value={repeat.gapMode} onChange={(v) => updateDeep('repeat', { gapMode: v })}
                options={[{ value: 'off', label: 'None' }, { value: 'x1', label: '1×' }, { value: 'x1.5', label: '1.5×' }, { value: 'x2', label: '2×' }, { value: 'fixed', label: 'Fixed' }]} />
              {repeat.gapMode === 'fixed' && (
                <div className="flex items-center justify-between"><span className="text-muted">Seconds</span><Stepper value={repeat.gapSeconds} min={1} max={60} onChange={(v) => updateDeep('repeat', { gapSeconds: v })} /></div>
              )}
            </div>
            <Row icon={<Repeat size={18} />} label="Repeat each āyah"><RepeatStepper label="āyah repeats" value={repeat.ayahRepeat} onChange={(v) => updateDeep('repeat', { ayahRepeat: v })} /></Row>
            <div className="row flex-col !items-stretch gap-2">
              <span className="text-[15px]">What to repeat</span>
              <Segmented label="Repeat scope" value={p.range ? 'range' : 'surah'} onChange={(v) => {
                if (v === 'surah') p.setRange(null);
                else { const cur = p.current.verseKey ? Number(p.current.verseKey.split(':')[1]) : 1; applyRange(cur, Math.min(ch.v, cur + 4)); }
              }} options={[{ value: 'surah', label: 'Whole surah' }, { value: 'range', label: 'Āyah to āyah' }]} />
            </div>
            {p.range && (
              <>
                <Row label="From āyah"><NumberField label="From āyah" value={range.from} min={1} max={ch.v} onChange={(v) => applyRange(v, Math.max(v, range.to))} /></Row>
                <Row label="To āyah"><NumberField label="To āyah" value={range.to} min={1} max={ch.v} onChange={(v) => applyRange(Math.min(range.from, v), v)} /></Row>
                <p className="px-4 pb-3 text-[12px] text-muted">{ch.n} has {ch.v} āyāt — type a number or use − / +.</p>
              </>
            )}
            <Row label={p.range ? `Repeat āyāt ${range.from}–${range.to}` : `Repeat ${ch.n}`}>
              <RepeatStepper label={p.range ? 'range repeats' : 'surah repeats'} value={repeat.rangeRepeat} onChange={(v) => updateDeep('repeat', { rangeRepeat: v })} />
            </Row>
          </>
        )}
      </Section>

      <Section title="Speed">
        <div className="row flex-col !items-stretch">
          <div className="flex justify-between text-[15px]"><span>Playback speed</span><span className="tabular-nums text-muted">{repeat.rate.toFixed(2)}×</span></div>
          <Range label="Playback speed" min={0.5} max={1.75} step={0.05} value={repeat.rate} onChange={(v) => updateDeep('repeat', { rate: v })} />
        </div>
      </Section>

      <Section>
        <Row label="Open in mushaf" onClick={() => { p.setSheet(false); nav(`/quran/${p.surah}${p.current.verseKey ? `?ayah=${p.current.verseKey.split(':')[1]}` : ''}`); }} />
      </Section>
      <p className="px-8 pb-2 text-[12px] text-muted">{r.credit}</p>

      <ReciterPickerSheet open={pickReciter} onClose={() => setPickReciter(false)} surah={p.surah} onPick={(id) => { void p.setReciter(id); setPickReciter(false); }} />
    </Sheet>
  );
}

export function FavoriteButton({ id, name, className = '' }: { id: string; name: string; className?: string }) {
  const favs = useSettings((s) => s.settings.favoriteReciters);
  const update = useSettings((s) => s.update);
  const on = favs.includes(id);
  return (
    <button type="button" aria-pressed={on} aria-label={on ? `Remove ${name} from favourites` : `Add ${name} to favourites`}
      onClick={(e) => { e.stopPropagation(); haptic(on ? 6 : 14); update({ favoriteReciters: on ? favs.filter((f) => f !== id) : [...favs, id] }); }}
      className={`press grid h-10 w-10 shrink-0 place-items-center rounded-full ${on ? 'text-gold' : 'text-faint'} ${className}`}>
      <Star size={20} fill={on ? 'currentColor' : 'none'} strokeWidth={1.8} />
    </button>
  );
}

export type ReciterFilter = 'all' | 'female' | 'male' | 'favorites';

export function ReciterList({ surah, selected, onPick, filter }: { surah?: number; selected?: string; onPick: (id: string) => void; filter: ReciterFilter }) {
  const favs = useSettings((s) => s.settings.favoriteReciters);
  const can = useCanHear();
  const sorted = [...RECITERS].filter(can).sort(byName);
  const list = sorted.filter((r) => filter === 'all' || filter === 'favorites' || r.gender === filter);
  const favourites = sorted.filter((r) => favs.includes(r.id));
  const women = filter === 'favorites' ? [] : list.filter((r) => r.gender === 'female');
  const men = filter === 'favorites' ? [] : list.filter((r) => r.gender === 'male');
  const renderGroup = (title: string, items: Reciter[], footer?: string) => items.length > 0 && (
    <Section title={title} footer={footer}>
      {items.map((r) => {
        const unavailable = surah != null && r.surahs != null && !r.surahs.includes(surah);
        return (
          <div key={r.id} className={`row !pr-2 ${unavailable ? 'opacity-40' : ''}`}>
          <button type="button" disabled={unavailable} onClick={() => { haptic(); onPick(r.id); }}
            className="press flex min-w-0 flex-1 items-center gap-3 text-left">
            <ReciterAvatar r={r} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[16px] font-semibold">{r.name}</span>
              <span className="block truncate text-[13px] text-muted">
                {[r.country, r.style, r.riwayah && !r.riwayah.startsWith('Hafs') ? `riwāyah ${r.riwayah}` : null, r.audio === 'clips' ? `${r.clips.length} recording${r.clips.length > 1 ? 's' : ''}` : r.surahs ? `${r.surahs.length} surah${r.surahs.length > 1 ? 's' : ''}` : null, r.timing === 'word' ? 'word highlight' : r.timing === 'ayah' ? 'āyah highlight' : null].filter(Boolean).join(' · ')}
              </span>
            </span>
            {r.nameAr && <span className="hidden font-arabic text-[16px] text-muted sm:inline" dir="rtl">{r.nameAr}</span>}
            {selected === r.id && <span className="h-2.5 w-2.5 rounded-full bg-accent" aria-label="Selected" />}
          </button>
          <FavoriteButton id={r.id} name={r.name} />
          </div>
        );
      })}
    </Section>
  );
  return (
    <>
      {(filter === 'all' || filter === 'favorites') && renderGroup('Favourites', favourites.filter((r) => filter === 'favorites' || true))}
      {filter === 'favorites' && favourites.length === 0 && <p className="px-8 py-10 text-center text-[15px] text-muted">Tap the ☆ next to a reciter to add them to your favourites.</p>}
      {renderGroup('Women reciters · قارئات', women, 'Recordings by women qāriʾāt. Some cover selected surahs; we are adding more as recordings are licensed.')}
      {renderGroup('Reciters · قرّاء', men)}
    </>
  );
}

export function ReciterPickerSheet({ open, onClose, onPick, surah }: { open: boolean; onClose: () => void; onPick: (id: string) => void; surah?: number }) {
  const [filter, setFilter] = useState<ReciterFilter>('all');
  const selected = usePlayer((s) => s.reciterId);
  const sister = useSettings((s) => s.settings.gender === 'female');
  return (
    <Sheet open={open} onClose={onClose} title="Choose reciter">
      <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 pb-4">
        <Chip active={filter === 'all'} onClick={() => setFilter('all')}>All</Chip>
        <Chip active={filter === 'favorites'} onClick={() => setFilter('favorites')}>★ Favourites</Chip>
        {sister && <Chip active={filter === 'female'} onClick={() => setFilter('female')}>Women</Chip>}
        {sister && <Chip active={filter === 'male'} onClick={() => setFilter('male')}>Men</Chip>}
      </div>
      <ReciterList surah={surah} selected={selected} onPick={onPick} filter={filter} />
    </Sheet>
  );
}
